import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { FileText, DownloadSimple, EnvelopeSimple, ClockClockwise, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

export default function AdminStatementsPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedStore, setSelectedStore] = useState(null);
  const [storeDetail, setStoreDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [showSendDialog, setShowSendDialog] = useState(false);
  const [sendingStore, setSendingStore] = useState(null);
  const [overrideEmail, setOverrideEmail] = useState("");

  const [showRuns, setShowRuns] = useState(false);
  const [runs, setRuns] = useState([]);

  const getMonthUtcRange = useCallback(() => {
    const startLocal = new Date(year, month - 1, 1, 0, 0, 0);
    const endLocal = new Date(year, month, 0, 23, 59, 59);
    return { start_utc: startLocal.toISOString(), end_utc: endLocal.toISOString() };
  }, [year, month]);

  const fetchStatements = useCallback(async () => {
    setLoading(true);
    try {
      const { start_utc, end_utc } = getMonthUtcRange();
      const { data } = await axios.get(`${API}/admin/statements?year=${year}&month=${month}&start_utc=${start_utc}&end_utc=${end_utc}`, { withCredentials: true });
      setStores(data.stores || []);
    } catch { toast.error("Failed to load statements"); }
    setLoading(false);
  }, [year, month, getMonthUtcRange]);

  useEffect(() => { fetchStatements(); }, [fetchStatements]);

  const keyFor = (s) => s.org_id || s.user_id;

  const viewDetail = async (store) => {
    setSelectedStore(store);
    setDetailLoading(true);
    try {
      const { start_utc, end_utc } = getMonthUtcRange();
      const { data } = await axios.get(`${API}/admin/statements/${keyFor(store)}?year=${year}&month=${month}&start_utc=${start_utc}&end_utc=${end_utc}`, { withCredentials: true });
      setStoreDetail(data);
    } catch { toast.error("Failed to load details"); }
    setDetailLoading(false);
  };

  const downloadFile = async (store, kind) => {
    const { start_utc, end_utc } = getMonthUtcRange();
    const url = `${API}/admin/statements/${keyFor(store)}/${kind}?year=${year}&month=${month}&start_utc=${start_utc}&end_utc=${end_utc}`;
    const safeName = (store.store_name || "statement").replace(/\s+/g, "_");
    try {
      const res = await axios.get(url, { withCredentials: true, responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `statement-${safeName}-${year}-${month}.${kind}`;
      link.click();
      URL.revokeObjectURL(blobUrl);
      toast.success(`${kind.toUpperCase()} downloaded`);
    } catch { toast.error("Failed to download"); }
  };

  const openSend = (store) => {
    if (!store.org_id) { toast.error("Walk-in orders cannot be auto-emailed"); return; }
    setSendingStore(store);
    setOverrideEmail(store.billing_email || "");
    setShowSendDialog(true);
  };

  const handleSend = async () => {
    if (!sendingStore) return;
    try {
      const { data } = await axios.post(`${API}/admin/statements/${sendingStore.org_id}/send?year=${year}&month=${month}`, { override_email: overrideEmail }, { withCredentials: true });
      if (data?.skipped) {
        toast.info(data?.reason === "no orders" ? "No orders for this period — nothing sent" : "Skipped");
        setShowSendDialog(false);
        return;
      }
      const stub = data?.result?.result?.stub;
      toast.success(stub ? `Statement queued (email stub — add API key to actually send) — ${data.recipient}` : `Statement sent to ${data.recipient}`);
      setShowSendDialog(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };

  const openRuns = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/statements/runs?year=${year}&month=${month}`, { withCredentials: true });
      setRuns(data.runs || []);
      setShowRuns(true);
    } catch { toast.error("Failed to load history"); }
  };

  const grandTotal = stores.reduce((s, st) => s + st.total, 0);
  const totalOrders = stores.reduce((s, st) => s + st.order_count, 0);
  const years = []; for (let y = 2024; y <= now.getFullYear() + 1; y++) years.push(y);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="statements-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">ADMIN</p>
          <h1 className="font-[Chivo] text-3xl sm:text-4xl font-black tracking-tighter text-zinc-950 mt-1">Monthly Statements</h1>
          <p className="text-xs text-zinc-500 mt-1">Auto-emailed to clinic billing addresses on the 1st of each month at 9am AEST</p>
        </div>
        <button onClick={openRuns} className="px-3 py-2 text-xs font-mono uppercase tracking-wider border border-zinc-300 hover:border-zinc-950 flex items-center gap-1" data-testid="view-runs-btn">
          <ClockClockwise size={14} /> Send History
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <Select value={String(month)} onValueChange={v => setMonth(parseInt(v))}>
          <SelectTrigger className="w-44 rounded-none" data-testid="statement-month-select"><SelectValue /></SelectTrigger>
          <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={String(year)} onValueChange={v => setYear(parseInt(v))}>
          <SelectTrigger className="w-28 rounded-none" data-testid="statement-year-select"><SelectValue /></SelectTrigger>
          <SelectContent>{years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-3 gap-0 border border-zinc-200 mb-6">
        <div className="p-5 border-r border-zinc-200">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Total Revenue</p>
          <p className="font-[Chivo] text-2xl font-black text-zinc-950 mt-1">${grandTotal.toFixed(2)}</p>
        </div>
        <div className="p-5 border-r border-zinc-200">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Total Orders</p>
          <p className="font-[Chivo] text-2xl font-black text-zinc-950 mt-1">{totalOrders}</p>
        </div>
        <div className="p-5">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Clinics</p>
          <p className="font-[Chivo] text-2xl font-black text-zinc-950 mt-1">{stores.length}</p>
        </div>
      </div>

      <div className="border border-zinc-200" data-testid="statements-table">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500">Clinic / User</th>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500">Billing Email</th>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500 text-center">Orders</th>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500 text-right">Total</th>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500 w-72">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="py-12 text-center text-sm text-zinc-500 font-mono">LOADING...</td></tr>
              : stores.length === 0 ? <tr><td colSpan={5} className="py-12 text-center text-sm text-zinc-500">No orders for this period</td></tr>
              : stores.map((store, i) => (
                <tr key={i} className="border-b border-zinc-200 hover:bg-zinc-50 transition-colors" data-testid={`statement-row-${i}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      {store.org_id && <Buildings size={14} className="text-[#0055FF]" weight="fill" />}
                      <div>
                        <p className="text-sm font-medium text-zinc-800">{store.store_name}</p>
                        {store.org_code && <p className="font-mono text-[10px] uppercase tracking-wider text-zinc-400">{store.org_code}</p>}
                        {!store.org_id && <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">Walk-in</p>}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-xs text-zinc-600 font-mono">{store.billing_email || (store.org_id ? <span className="text-amber-600">— not set —</span> : "—")}</td>
                  <td className="py-3 px-4 text-sm text-zinc-800 text-center font-mono">{store.order_count}</td>
                  <td className="py-3 px-4 text-sm font-bold text-zinc-950 text-right font-mono">${store.total.toFixed(2)}</td>
                  <td className="py-3 px-4">
                    <div className="flex gap-1 flex-wrap">
                      <button onClick={() => viewDetail(store)} className="px-2 py-1.5 text-xs font-medium bg-[#0055FF] text-white hover:bg-[#0044CC] flex items-center gap-1" data-testid={`view-statement-${i}`}>
                        <FileText size={12} /> View
                      </button>
                      <button onClick={() => downloadFile(store, "pdf")} className="px-2 py-1.5 text-xs font-medium bg-zinc-100 text-zinc-700 border border-zinc-200 hover:bg-zinc-200 flex items-center gap-1" data-testid={`download-pdf-${i}`}>
                        <DownloadSimple size={12} /> PDF
                      </button>
                      <button onClick={() => downloadFile(store, "csv")} className="px-2 py-1.5 text-xs font-medium bg-zinc-100 text-zinc-700 border border-zinc-200 hover:bg-zinc-200 flex items-center gap-1" data-testid={`download-csv-${i}`}>
                        <DownloadSimple size={12} /> CSV
                      </button>
                      {store.org_id && (
                        <button onClick={() => openSend(store)} className="px-2 py-1.5 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 flex items-center gap-1" data-testid={`send-statement-${i}`}>
                          <EnvelopeSimple size={12} /> Send
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Send Dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent className="sm:max-w-md" data-testid="send-statement-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Send Statement</DialogTitle></DialogHeader>
          {sendingStore && (
            <div className="space-y-3">
              <div className="p-3 border border-zinc-200 bg-zinc-50">
                <p className="text-sm font-bold text-zinc-950">{sendingStore.store_name}</p>
                <p className="text-xs text-zinc-500 font-mono mt-0.5">{MONTHS[month - 1]} {year} · {sendingStore.order_count} orders · ${sendingStore.total.toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Recipient Email</Label>
                <Input value={overrideEmail} onChange={e => setOverrideEmail(e.target.value)} className="mt-1.5 rounded-none" placeholder="billing@clinic.com" data-testid="send-email-input" />
                <p className="text-[10px] text-zinc-500 mt-1">PDF + CSV will be attached. Email is currently in stub mode (logged only) until API key is provided.</p>
              </div>
              <button onClick={handleSend} className="w-full bg-[#0055FF] text-white font-semibold py-3 hover:bg-[#0044CC] flex items-center justify-center gap-2" data-testid="confirm-send-statement">
                <EnvelopeSimple size={16} /> SEND STATEMENT
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send History Dialog */}
      <Dialog open={showRuns} onOpenChange={setShowRuns}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="runs-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Send History — {MONTHS[month - 1]} {year}</DialogTitle></DialogHeader>
          {runs.length === 0 ? <p className="text-sm text-zinc-500 text-center py-8">No statements sent yet for this period</p> :
            <div className="border border-zinc-200 divide-y divide-zinc-100">
              {runs.map(r => (
                <div key={r.id} className="p-3 flex items-center justify-between text-sm">
                  <div>
                    <p className="font-medium text-zinc-950 font-mono text-xs">{r.recipient}</p>
                    <p className="text-xs text-zinc-500">{new Date(r.sent_at).toLocaleString()}</p>
                  </div>
                  <div className="text-right">
                    <span className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border ${r.status === "sent" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : r.status === "stub" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-red-50 text-red-700 border-red-200"}`}>{r.status}</span>
                    <p className="text-xs font-mono text-zinc-700 mt-1">${r.grand_total?.toFixed(2)} · {r.order_count} orders</p>
                  </div>
                </div>
              ))}
            </div>
          }
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!selectedStore} onOpenChange={(open) => { if (!open) { setSelectedStore(null); setStoreDetail(null); } }}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="statement-detail-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Statement: {selectedStore?.store_name}</DialogTitle></DialogHeader>
          {detailLoading ? <p className="py-8 text-center font-mono text-sm text-zinc-500">LOADING...</p>
            : storeDetail ? (
            <div className="space-y-4">
              <div className="bg-zinc-50 p-3 border border-zinc-200">
                <p className="text-xs font-mono text-zinc-500">PERIOD: {storeDetail.period}</p>
                <p className="text-sm font-medium text-zinc-950 mt-1">{storeDetail.store_name}</p>
                <p className="text-xs text-zinc-500 mt-1">{storeDetail.order_count} orders · billing: {storeDetail.store_email || "—"}</p>
              </div>
              <div className="border border-zinc-200 overflow-x-auto">
                <table className="w-full border-collapse text-left text-sm">
                  <thead>
                    <tr>
                      <th className="border-b border-zinc-300 py-2 px-3 font-mono text-xs text-zinc-500">Date</th>
                      <th className="border-b border-zinc-300 py-2 px-3 font-mono text-xs text-zinc-500">Order</th>
                      <th className="border-b border-zinc-300 py-2 px-3 font-mono text-xs text-zinc-500">Patient</th>
                      <th className="border-b border-zinc-300 py-2 px-3 font-mono text-xs text-zinc-500 text-center">Items</th>
                      <th className="border-b border-zinc-300 py-2 px-3 font-mono text-xs text-zinc-500 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {storeDetail.orders?.map((o, i) => (
                      <tr key={i} className="border-b border-zinc-100">
                        <td className="py-2 px-3 text-zinc-600">{o.created_at?.slice(0, 10)}</td>
                        <td className="py-2 px-3 font-mono text-xs text-zinc-500">#{o.id?.slice(0, 8)}</td>
                        <td className="py-2 px-3 text-zinc-800">{o.patient_name}</td>
                        <td className="py-2 px-3 text-center text-zinc-600">{o.items?.length}</td>
                        <td className="py-2 px-3 text-right font-mono font-semibold">${o.total?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="bg-zinc-50 p-4 border border-zinc-200 space-y-1">
                <div className="flex justify-between text-sm"><span className="text-zinc-500">Products Subtotal</span><span className="font-mono">${storeDetail.subtotal?.toFixed(2)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-zinc-500">Delivery Total</span><span className="font-mono">${storeDetail.delivery_total?.toFixed(2)}</span></div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-zinc-200"><span>Grand Total</span><span className="font-mono">${storeDetail.grand_total?.toFixed(2)}</span></div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => downloadFile(selectedStore, "pdf")} className="flex-1 bg-[#0055FF] text-white font-semibold py-3 hover:bg-[#0044CC] flex items-center justify-center gap-2"><DownloadSimple size={18} /> PDF</button>
                <button onClick={() => downloadFile(selectedStore, "csv")} className="flex-1 bg-zinc-100 text-zinc-700 border border-zinc-200 font-semibold py-3 hover:bg-zinc-200 flex items-center justify-center gap-2"><DownloadSimple size={18} /> CSV</button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
